import { useEffect, useState, useRef } from 'react';
import { AppState, AppStateStatus, Platform } from 'react-native';
import { supabase } from '../config/supabase';

const CHECK_INTERVAL_MS = 30000; // Minimum 30s between checks

export const useSessionGuard = (isAuthenticated: boolean) => {
  const [showDialog, setShowDialog] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const lastCheckTime = useRef<number>(0);
  const isChecking = useRef<boolean>(false);

  // Validate session and show dialog if expired
  const checkSession = async () => {
    // Only check session if user is authenticated
    if (!isAuthenticated) {
      return;
    }
    // Prevent spam checks
    const now = Date.now();
    if (now - lastCheckTime.current < CHECK_INTERVAL_MS) {
      return;
    }

    // Prevent concurrent checks
    if (isChecking.current) {
      return;
    }

    isChecking.current = true;
    lastCheckTime.current = now;

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        // Session expired - get last known email for pre-fill
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.email) {
          setUserEmail(user.email);
        }
        setShowDialog(true);
      }
    } catch (error) {
      console.error('Session validation error:', error);
      // Don't show dialog on network errors - could be temporary
    } finally {
      isChecking.current = false;
    }
  };

  // Mobile: AppState listener
  useEffect(() => {
    if (Platform.OS === 'web') return;

    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      if (nextAppState === 'active') {
        checkSession();
      }
    };

    const subscription = AppState.addEventListener('change', handleAppStateChange);

    return () => {
      subscription.remove();
    };
  }, []);

  // Web: Visibility API listener
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Re-auth success handler
  const handleReAuthSuccess = () => {
    setShowDialog(false);
    setUserEmail('');
  };

  // Return dialog state for parent to render
  return {
    showDialog,
    userEmail,
    onSuccess: handleReAuthSuccess,
  };
};
